import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart3, TrendingUp, DollarSign, Building2 } from "lucide-react";
import { subDays, startOfDay, format, startOfMonth, endOfMonth, subMonths } from "date-fns";
import { ar } from "date-fns/locale";
import { CURRENCY_SYMBOL, formatCurrency } from "@/lib/currency";

const AccountantReports = () => {
  const { data: invoices } = useQuery({
    queryKey: ["accountant-reports-invoices"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invoices")
        .select("id, amount, status, paid_at, created_at, due_date, nursery_id, nurseries:nursery_id(name)")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const { data: subscriptions } = useQuery({
    queryKey: ["accountant-reports-subs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("nursery_subscriptions")
        .select("id, amount, status, plan, started_at, nurseries:nursery_id(name)")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const all = invoices || [];
  const paidInvoices = all.filter(i => i.status === "paid");

  // Monthly revenue for last 6 months
  const monthlyData: { month: string; revenue: number; count: number }[] = [];
  for (let i = 5; i >= 0; i--) {
    const d = subMonths(new Date(), i);
    const start = startOfMonth(d);
    const end = endOfMonth(d);
    const monthPaid = paidInvoices.filter(inv => {
      const paidDate = inv.paid_at ? new Date(inv.paid_at) : null;
      return paidDate && paidDate >= start && paidDate <= end;
    });
    monthlyData.push({
      month: format(d, "MMM yyyy", { locale: ar }),
      revenue: monthPaid.reduce((s, inv) => s + (inv.amount || 0), 0),
      count: monthPaid.length,
    });
  }
  const maxMonthlyRevenue = Math.max(...monthlyData.map(m => m.revenue), 1);

  // Revenue by nursery
  const nurseryRevenue: Record<string, number> = {};
  paidInvoices.forEach((inv: any) => {
    const name = inv.nurseries?.name || "غير محدد";
    nurseryRevenue[name] = (nurseryRevenue[name] || 0) + (inv.amount || 0);
  });
  const nurseryRevenueArr = Object.entries(nurseryRevenue).sort((a, b) => b[1] - a[1]);
  const maxNurseryRevenue = Math.max(...nurseryRevenueArr.map(([, v]) => v), 1);

  // Plan distribution
  const planCount: Record<string, number> = {};
  (subscriptions || []).filter(s => s.status === "active").forEach(s => {
    planCount[s.plan] = (planCount[s.plan] || 0) + 1;
  });
  const planLabels: Record<string, string> = {
    free: "مجاني", basic: "أساسي", premium: "متقدم", enterprise: "مؤسسي",
  };
  const planColors: Record<string, string> = {
    free: "bg-muted-foreground", basic: "bg-blue-500", premium: "bg-primary", enterprise: "bg-amber-500",
  };
  const totalActiveSubs = Object.values(planCount).reduce((a, b) => a + b, 0) || 1;

  // Collection rate
  const totalInvoiced = all.reduce((s, i) => s + (i.amount || 0), 0);
  const totalCollected = paidInvoices.reduce((s, i) => s + (i.amount || 0), 0);
  const collectionRate = totalInvoiced > 0 ? Math.round((totalCollected / totalInvoiced) * 100) : 0;

  return (
    <div className="space-y-6" dir="rtl">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <BarChart3 className="w-6 h-6 text-primary" />
          التقارير المالية
        </h1>
        <p className="text-muted-foreground text-sm mt-1">تحليل الإيرادات والتحصيلات</p>
      </div>

      {/* Top Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground">إجمالي الفوترة</p>
            <p className="text-2xl font-bold">{totalInvoiced.toLocaleString("ar-EG")} <span className="text-sm">{CURRENCY_SYMBOL}</span></p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground">المحصّل</p>
            <p className="text-2xl font-bold text-green-600">{totalCollected.toLocaleString("ar-EG")} <span className="text-sm">{CURRENCY_SYMBOL}</span></p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground">نسبة التحصيل</p>
            <p className="text-2xl font-bold text-primary">{collectionRate}%</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground">فواتير مدفوعة</p>
            <p className="text-2xl font-bold">{paidInvoices.length} / {all.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Revenue Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            الإيرادات الشهرية (آخر 6 أشهر)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-4 h-48">
            {monthlyData.map((m, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-xs font-bold">{m.revenue.toLocaleString()}</span>
                <span className="text-[10px] text-muted-foreground">{m.count} فاتورة</span>
                <div
                  className="w-full bg-primary/80 rounded-t-md transition-all"
                  style={{ height: `${(m.revenue / maxMonthlyRevenue) * 100}%`, minHeight: m.revenue > 0 ? "8px" : "2px" }}
                />
                <span className="text-[10px] text-muted-foreground">{m.month}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Revenue by Nursery */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              الإيرادات حسب الحضانة
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {nurseryRevenueArr.slice(0, 10).map(([name, revenue]) => (
              <div key={name} className="flex items-center justify-between">
                <span className="text-sm truncate max-w-[50%]">{name}</span>
                <div className="flex items-center gap-2">
                  <div className="w-28 h-2.5 bg-muted rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-primary" style={{ width: `${(revenue / maxNurseryRevenue) * 100}%` }} />
                  </div>
                  <span className="text-sm font-medium w-24 text-left">{formatCurrency(revenue, { decimals: 0 })}</span>
                </div>
              </div>
            ))}
            {!nurseryRevenueArr.length && (
              <p className="text-center py-4 text-muted-foreground text-sm">لا توجد بيانات</p>
            )}
          </CardContent>
        </Card>

        {/* Plan Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              توزيع خطط الاشتراك (النشطة)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {["enterprise", "premium", "basic", "free"].map(plan => (
              <div key={plan} className="flex items-center justify-between">
                <span className="text-sm font-medium">{planLabels[plan]}</span>
                <div className="flex items-center gap-2">
                  <div className="w-28 h-2.5 bg-muted rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${planColors[plan]}`} style={{ width: `${((planCount[plan] || 0) / totalActiveSubs) * 100}%` }} />
                  </div>
                  <span className="text-sm font-medium w-8 text-left">{planCount[plan] || 0}</span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AccountantReports;
